import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Play, Square, Settings, TrendingUp, TrendingDown, Activity, Zap, Wifi, WifiOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Convert HTTP URL to WebSocket URL
const WS_URL = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const DashboardPage = ({ onLogout }) => {
  // Session & Auth state
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random()}`);
  const [authenticated, setAuthenticated] = useState(false);
  const [apiToken, setApiToken] = useState('');
  const [accountIdInput, setAccountIdInput] = useState('');  // Optional: explicit Deriv account ID (for PAT tokens)
  const [authLoading, setAuthLoading] = useState(false);

  // WebSocket state
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  // True from the moment the effect starts tearing down. Guards against the
  // WS onclose handler scheduling reconnects and against stray setState calls
  // from a WS that belongs to a previous effect run.
  const wsMountedRef = useRef(false);

  // Bot state
  const [botRunning, setBotRunning] = useState(false);
  const [balance, setBalance] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [accountId, setAccountId] = useState('');
  const [userName, setUserName] = useState('');

  // Config state
  const [stake, setStake] = useState(0.35);
  const [martingaleMultiplier, setMartingaleMultiplier] = useState(1.35);
  const [martingaleEnabled, setMartingaleEnabled] = useState(true);
  const [martingaleMaxStake, setMartingaleMaxStake] = useState(0);  // 0 = no limit
  const [minMomentum, setMinMomentum] = useState(0);  // 0 = any positive momentum
  const [rsiFilter, setRsiFilter] = useState('off');  // "off", "under_75", "over_75"
  const [smartTrading, setSmartTrading] = useState(true);
  const [digitMatchMode, setDigitMatchMode] = useState(false);  // Digit Match Streak strategy
  const [digitXxFilter, setDigitXxFilter] = useState('any');   // "any" | "high" | "low"
  const [aiDigitMode, setAiDigitMode] = useState(false);       // AI devises its own digit strategy
  const [recentDigits, setRecentDigits] = useState({});
  const [autoSwitchMarket, setAutoSwitchMarket] = useState(true);  // Auto switch between markets
  const [selectedMarket, setSelectedMarket] = useState('R_10');  // Selected market when auto switch is OFF
  const [autoBestContract, setAutoBestContract] = useState(false);  // Auto-select best contract
  const [autoRestart, setAutoRestart] = useState(false);  // Auto restart when TP/SL hit
  const [takeProfit, setTakeProfit] = useState(100.0);
  const [stopLoss, setStopLoss] = useState(50.0);
  
  const [scalperMode, setScalperMode] = useState(false);  // Scalper mode
  const [trendRiderMode, setTrendRiderMode] = useState(false);  // Trend Rider mode
  const [aiTrendMode, setAiTrendMode] = useState(false);        // AI trend-following with pullback entries
  const [accumulatorMode, setAccumulatorMode] = useState(false); // Accumulator (ACCU) trading
  const [accumulatorGrowthRate, setAccumulatorGrowthRate] = useState(0.01); // 0.01 / 0.02 / 0.03 / 0.04 / 0.05
  const [accumulatorTpPercent, setAccumulatorTpPercent] = useState(0.10);   // take-profit as fraction of stake
  const [compoundMode, setCompoundMode] = useState(false);  // Compound Trading (auto-stake by confidence + win-streak compounding)
  const [compoundMartingale, setCompoundMartingale] = useState(false);  // Layer martingale on top of compound
  const [closingContracts, setClosingContracts] = useState(new Set()); // contract_ids currently being closed

  // Mirror accounts (Settings page)
  const [showSettings, setShowSettings] = useState(false);
  const [mirrors, setMirrors] = useState([]);
  const [maxMirrors, setMaxMirrors] = useState(5);
  const [newMirrorToken, setNewMirrorToken] = useState('');
  const [newMirrorAccountId, setNewMirrorAccountId] = useState('');
  const [newMirrorLabel, setNewMirrorLabel] = useState('');
  const [mirrorAdding, setMirrorAdding] = useState(false);
  const [mirrorError, setMirrorError] = useState('');

  const fetchMirrors = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/mirrors/list/${sessionId}`);
      setMirrors(res.data.mirrors || []);
      if (res.data.max_allowed) setMaxMirrors(res.data.max_allowed);
    } catch (e) { /* silent */ }
  };

  const addMirror = async () => {
    setMirrorError('');
    const cleanToken = (newMirrorToken || '').replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, '').trim();
    if (!cleanToken) { setMirrorError('Please paste a valid Deriv API token'); return; }
    setMirrorAdding(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/mirrors/add`, {
        session_id: sessionId,
        token: cleanToken,
        account_id: (newMirrorAccountId || '').trim() || null,
        label: (newMirrorLabel || '').trim() || null,
      });
      if (res.data.success) {
        setNewMirrorToken('');
        setNewMirrorAccountId('');
        setNewMirrorLabel('');
        await fetchMirrors();
      } else {
        setMirrorError(res.data.error || 'Failed to add mirror');
      }
    } catch (e) {
      setMirrorError(e.message);
    } finally {
      setMirrorAdding(false);
    }
  };

  const removeMirror = async (mirrorId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/mirrors/remove`, {
        session_id: sessionId,
        mirror_id: mirrorId,
      });
      await fetchMirrors();
    } catch (e) { /* silent */ }
  };

  // Available markets
  const availableMarkets = [
    { value: 'R_10', label: 'Volatility 10 Index' },
    { value: 'R_25', label: 'Volatility 25 Index' },
    { value: 'R_50', label: 'Volatility 50 Index' },
    { value: 'R_75', label: 'Volatility 75 Index' },
    { value: 'R_100', label: 'Volatility 100 Index' },
    { value: '1HZ10V', label: 'Volatility 10 (1s)' },
    { value: '1HZ25V', label: 'Volatility 25 (1s)' },
    { value: '1HZ50V', label: 'Volatility 50 (1s)' },
    { value: '1HZ75V', label: 'Volatility 75 (1s)' },
    { value: '1HZ100V', label: 'Volatility 100 (1s)' },
  ];

  // Data state
  const [stats, setStats] = useState({});
  const [marketReadiness, setMarketReadiness] = useState({});
  const [trades, setTrades] = useState([]);
  const [logs, setLogs] = useState([]);
  const [analyzedMarkets, setAnalyzedMarkets] = useState({});

  // Keep-alive ping to prevent server sleep
  useEffect(() => {
    const keepAlive = async () => {
      try {
        await axios.get(`${BACKEND_URL}/api/keep-alive`);
      } catch (e) {
        console.log('[KEEP-ALIVE] Ping failed');
      }
    };
    
    // Ping every 30 seconds
    const interval = setInterval(keepAlive, 30000);
    keepAlive(); // Initial ping
    
    return () => clearInterval(interval);
  }, []);

  // WebSocket connection and message handling
  useEffect(() => {
    if (!authenticated) return;
    wsMountedRef.current = true;

    const connectWebSocket = () => {
      if (!wsMountedRef.current) return;
      try {
        const ws = new WebSocket(`${WS_URL}/ws/${sessionId}`);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!wsMountedRef.current || wsRef.current !== ws) return;
          console.log('[WS] Connected');
          setWsConnected(true);
        };

        ws.onmessage = (event) => {
          if (!wsMountedRef.current || wsRef.current !== ws) return;
          try {
            const data = JSON.parse(event.data);
            
            // Handle different message types
            if (data.type === 'status' || data.type === 'update') {
              if (data.bot_running !== undefined) setBotRunning(data.bot_running);
              if (data.stats) {
                setStats(data.stats);
                if (data.stats.balance !== undefined) setBalance(data.stats.balance);
              }
              if (data.market_readiness) setMarketReadiness(data.market_readiness);
              if (data.logs) setLogs(data.logs);
              if (data.trades) setTrades(data.trades);
              if (data.analyzed_markets) setAnalyzedMarkets(data.analyzed_markets);
              if (data.recent_digits) setRecentDigits(data.recent_digits);
            } else if (data.type === 'market_data') {
              // Handle shared market data updates
              if (data.analysis) {
                setAnalyzedMarkets(prev => ({ ...prev, ...data.analysis }));
              }
            }
          } catch (e) {
            console.error('[WS] Parse error:', e);
          }
        };

        ws.onclose = () => {
          // Ignore closes from stale WS objects (previous effect run) —
          // otherwise they'd flip wsConnected off + schedule a reconnect
          // that races with the current live WS.
          if (wsRef.current !== ws) return;
          console.log('[WS] Disconnected');
          if (wsMountedRef.current) {
            setWsConnected(false);
          }
          // Only schedule a reconnect if we're still mounted
          if (wsMountedRef.current) {
            reconnectTimeoutRef.current = setTimeout(() => {
              if (wsMountedRef.current) {
                console.log('[WS] Attempting reconnect...');
                connectWebSocket();
              }
            }, 2000);
          }
        };

        ws.onerror = (error) => {
          console.error('[WS] Error:', error);
        };

      } catch (error) {
        console.error('[WS] Connection error:', error);
      }
    };

    connectWebSocket();

    // Cleanup on unmount
    return () => {
      wsMountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { /* noop */ }
        wsRef.current = null;
      }
    };
  }, [authenticated, sessionId]);

  // Polling — always on when authenticated. WebSocket updates are the fast
  // path; this 2s HTTP poll is a belt-and-suspenders fallback so the UI
  // stays populated even if the WS drops. Independent of wsConnected so the
  // effect doesn't remount on every WS reconnect (that was the flicker).
  useEffect(() => {
    if (!authenticated) return;

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/status/${sessionId}`);
        const data = response.data;

        setBotRunning(data.bot_running);
        setStats(data.stats || {});
        setMarketReadiness(data.market_readiness || {});
        setLogs(data.logs || []);
        setTrades(data.trades || []);
        setAnalyzedMarkets(data.analyzed_markets || {});
        setRecentDigits(data.recent_digits || {});
        
        if (data.stats?.balance !== undefined) {
          setBalance(data.stats.balance);
        }
      } catch (error) {
        console.error('Status poll error:', error);
      }
    };

    // Only poll when WS isn't feeding us. Using a ref check inside the
    // interval avoids re-mounting this effect on every WS toggle.
    const interval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === 1) return; // WS OPEN → skip poll
      pollStatus();
    }, 2000);
    return () => clearInterval(interval);
  }, [authenticated, sessionId]);

  const handleAuthenticate = async () => {
    // Sanitize: strip whitespace/newlines and zero-width chars often pasted from Deriv dashboard
    const cleanToken = (apiToken || '')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')   // zero-width chars
      .replace(/\s+/g, '')                       // any whitespace incl newlines/tabs
      .trim();
    if (!cleanToken) {
      alert('Please paste a valid Deriv API token.');
      return;
    }
    const cleanAccountId = (accountIdInput || '')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\s+/g, '')
      .trim();

    setAuthLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth`, {
        token: cleanToken,
        session_id: sessionId,
        account_id: cleanAccountId || null
      });

      if (response.data.success) {
        setAuthenticated(true);
        setBalance(response.data.balance);
        setCurrency(response.data.currency);
        setAccountId(response.data.account);
        setUserName(response.data.fullname || '');
      } else {
        alert(`Authentication failed: ${response.data.error}`);
      }
    } catch (error) {
      alert(`Connection error: ${error.message}`);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleStartBot = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/bot/control`, {
        session_id: sessionId,
        action: 'start'
      });
    } catch (error) {
      console.error('Start bot error:', error);
    }
  };

  const handleStopBot = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/bot/control`, {
        session_id: sessionId,
        action: 'stop'
      });
    } catch (error) {
      console.error('Stop bot error:', error);
    }
  };

  const handleUpdateConfig = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/config`, {
        session_id: sessionId,
        stake,
        martingale_multiplier: martingaleMultiplier,
        martingale_enabled: martingaleEnabled,
        martingale_max_stake: martingaleMaxStake,
        min_momentum: minMomentum,
        rsi_filter: rsiFilter,
        smart_trading: smartTrading,
        simple_mode: false,
        digit_match_mode: digitMatchMode,
        digit_xx_filter: digitXxFilter,
        ai_digit_mode: aiDigitMode,
        auto_switch_market: autoSwitchMarket,
        selected_market: selectedMarket,
        auto_best_contract: autoBestContract,
        auto_restart: autoRestart,
        take_profit: takeProfit,
        stop_loss: stopLoss,
        scalper_mode: scalperMode,
        trend_rider_mode: trendRiderMode,
        ai_trend_mode: aiTrendMode,
        accumulator_mode: accumulatorMode,
        accumulator_growth_rate: accumulatorGrowthRate,
        accumulator_tp_percent: accumulatorTpPercent,
        compound_mode: compoundMode,
        compound_martingale: compoundMartingale
      });
    } catch (error) {
      console.error('Config update error:', error);
    }
  };

  const winRate = useMemo(() => {
    if (!stats.total_trades || stats.total_trades === 0) return 0;
    return ((stats.wins / stats.total_trades) * 100).toFixed(1);
  }, [stats]);

  const handleCloseTrade = async (contractId) => {
    if (!contractId || closingContracts.has(contractId)) return;
    setClosingContracts((prev) => new Set(prev).add(contractId));
    try {
      await axios.post(`${BACKEND_URL}/api/close-trade`, {
        session_id: sessionId,
        contract_id: contractId,
      });
    } catch (error) {
      console.error('Close trade error:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Close failed';
      alert(`Close trade failed: ${msg}`);
    } finally {
      // Give the backend a moment to update the trade status, then clear the lock
      setTimeout(() => {
        setClosingContracts((prev) => {
          const next = new Set(prev);
          next.delete(contractId);
          return next;
        });
      }, 3000);
    }
  };

  const readinessColor = useMemo(() => {
    return marketReadiness.ready ? 'bg-green-500' : 'bg-red-500';
  }, [marketReadiness.ready]);

  const readinessText = useMemo(() => {
    return marketReadiness.ready ? 'READY' : 'NOT READY';
  }, [marketReadiness.ready]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900 p-2 overflow-hidden" data-testid="dashboard-page">
      {/* Compact Header */}
      <div className="max-w-7xl mx-auto mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-teal-400">GO TIME</h1>
            {/* Market Readiness Indicator */}
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right min-w-[100px]">
              <div className="text-xs text-gray-400">Balance</div>
              <div className="text-base font-bold text-teal-400 truncate" data-testid="balance-display">
                {balance >= 1000 ? `${(balance/1000).toFixed(1)}K` : balance.toFixed(2)} {currency}
              </div>
            </div>
            <Button
              onClick={() => { setShowSettings(true); fetchMirrors(); }}
              variant="outline"
              size="sm"
              className="border-teal-600 text-teal-400 hover:bg-teal-900 h-8 w-8 px-0"
              title="Settings"
              data-testid="open-settings-button"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="border-teal-600 text-teal-400 hover:bg-teal-900 h-8 px-2 text-xs"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-2 h-[calc(100vh-80px)]">
        {/* Left Column - Compact */}
        <div className="space-y-2 overflow-y-auto">
          {/* Connection Status */}
          <Card className="bg-gray-900 border-teal-700 p-2">
            <CardHeader className="p-2">
              <CardTitle className="text-sm text-teal-400">Connection</CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0">
              <div className="space-y-1 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className={`${authenticated ? 'text-green-400' : 'text-yellow-400'}`}>
                    {authenticated ? 'Connected' : 'Not Connected'}
                  </span>
                </div>
                {authenticated && userName && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Account</span>
                    <span className="text-teal-400 text-xs truncate max-w-[120px]" title={userName}>{userName}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Authentication */}
          {!authenticated && (
            <Card className="bg-gray-900 border-teal-700 p-2">
              <CardHeader className="p-2">
                <CardTitle className="text-sm text-teal-400">API Token</CardTitle>
              </CardHeader>
              <CardContent className="p-2 pt-0 space-y-2">
                <Input
                  type="password"
                  placeholder="API Token"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  className="bg-gray-800 border-teal-700 text-white text-xs h-8"
                  disabled={authLoading}
                  data-testid="api-token-input"
                />
                <Input
                  type="text"
                  placeholder="Account ID (optional, e.g. CR1234567)"
                  value={accountIdInput}
                  onChange={(e) => setAccountIdInput(e.target.value)}
                  className="bg-gray-800 border-teal-700 text-white text-xs h-8"
                  disabled={authLoading}
                  data-testid="account-id-input"
                />
                <Button
                  onClick={handleAuthenticate}
                  className="w-full bg-teal-600 hover:bg-teal-700 h-8 text-xs"
                  disabled={!apiToken || authLoading}
                  data-testid="authenticate-button"
                >
                  {authLoading ? 'Connecting...' : 'Connect'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Bot Controls */}
          {authenticated && (
            <Card className="bg-gray-900 border-teal-700 p-2">
              <CardHeader className="p-2">
                <CardTitle className="text-sm text-teal-400">Controls</CardTitle>
              </CardHeader>
              <CardContent className="p-2 pt-0 space-y-2">
                <div className="flex gap-1">
                  <Button
                    onClick={handleStartBot}
                    className="flex-1 bg-green-600 hover:bg-green-700 h-8 text-xs"
                    disabled={botRunning}
                    data-testid="start-bot-button"
                  >
                    <Play className="w-3 h-3 mr-1" />
                    Start
                  </Button>
                  <Button
                    onClick={handleStopBot}
                    className="flex-1 bg-red-600 hover:bg-red-700 h-8 text-xs"
                    disabled={!botRunning}
                    data-testid="stop-bot-button"
                  >
                    <Square className="w-3 h-3 mr-1" />
                    Stop
                  </Button>
                </div>
                <div className={`p-2 rounded text-center text-xs ${botRunning ? 'bg-green-900 text-green-400' : 'bg-gray-800 text-gray-400'}`}>
                  {botRunning ? 'Running' : 'Stopped'}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Configuration */}
          {authenticated && (
            <Card className="bg-gray-900 border-teal-700 p-2">
              <CardHeader className="p-2">
                <CardTitle className="text-sm text-teal-400 flex items-center gap-1">
                  <Settings className="w-3 h-3" />
                  Config
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 pt-0 space-y-2">
                {/* Smart Trading Toggle */}
                <div className="space-y-1">
                  <label className="text-xs text-gray-400 flex items-center justify-between">
                    <span>Smart Mode</span>
                    <button
                      onClick={() => setSmartTrading(!smartTrading)}
                      className={`relative w-8 h-4 rounded-full transition-colors ${smartTrading ? 'bg-teal-600' : 'bg-gray-600'}`}
                      data-testid="smart-trading-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${smartTrading ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                </div>

                {/* Auto Switch Market Toggle */}
                <div className="space-y-1">
                  <label className="text-xs text-gray-400 flex items-center justify-between">
                    <span>Auto Switch Market</span>
                    <button
                      onClick={() => setAutoSwitchMarket(!autoSwitchMarket)}
                      className={`relative w-8 h-4 rounded-full transition-colors ${autoSwitchMarket ? 'bg-blue-500' : 'bg-gray-600'}`}
                      data-testid="auto-switch-market-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${autoSwitchMarket ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                </div>

                {/* Market Selector - Shows when Auto Switch is OFF */}
                {!autoSwitchMarket && (
                  <div className="space-y-1 p-2 border border-blue-600 rounded bg-blue-900/20">
                    <label className="text-xs text-blue-400">Select Market</label>
                    <select
                      value={selectedMarket}
                      onChange={(e) => setSelectedMarket(e.target.value)}
                      className="w-full bg-gray-700 text-white text-xs p-2 rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                      data-testid="market-selector"
                    >
                      {availableMarkets.map((market) => (
                        <option key={market.value} value={market.value}>
                          {market.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Auto Best Contract Toggle */}
                <div className="space-y-1">
                  <label className="text-xs text-gray-400 flex items-center justify-between">
                    <span>Auto Best Contract</span>
                    <button
                      onClick={() => setAutoBestContract(!autoBestContract)}
                      className={`relative w-8 h-4 rounded-full transition-colors ${autoBestContract ? 'bg-yellow-500' : 'bg-gray-600'}`}
                      data-testid="auto-best-contract-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${autoBestContract ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                </div>

                {/* Auto Restart Toggle - Hands-free Trading */}
                <div className="space-y-1">
                  <label className="text-xs text-gray-400 flex items-center justify-between">
                    <span>Auto Restart (TP/SL)</span>
                    <button
                      onClick={() => setAutoRestart(!autoRestart)}
                      className={`relative w-8 h-4 rounded-full transition-colors ${autoRestart ? 'bg-green-500' : 'bg-gray-600'}`}
                      data-testid="auto-restart-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${autoRestart ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                </div>

                {/* COMPOUND TRADING - Auto-stake by confidence + win-streak compounding */}
                <div className="space-y-1 p-2 border-2 border-emerald-500 rounded bg-gradient-to-r from-emerald-900/40 to-green-900/30">
                  <label className="text-xs text-emerald-300 flex items-center justify-between">
                    <span>💰 Compound Trading</span>
                    <button
                      onClick={() => {
                        setCompoundMode(!compoundMode);
                        if (!compoundMode) {
                          setDigitMatchMode(false);
                          setScalperMode(false);
                          setTrendRiderMode(false);
                          setAccumulatorMode(false);
                        }
                      }}
                      className={`relative w-8 h-4 rounded-full transition-colors ${compoundMode ? 'bg-emerald-500' : 'bg-gray-600'}`}
                      data-testid="compound-mode-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${compoundMode ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                  <p className="text-xs text-emerald-200/70">
                    {compoundMode
                      ? (compoundMartingale
                          ? '🔥 Stake ×conf + win-streak bonus, layered ON martingale (increases after losses), capped at 5× base'
                          : '🔥 Stake ×1 (score 70-79), ×1.5 (80-89), ×2 (90+) + up to +50% win-streak bonus, capped at 3× base')
                      : 'Auto-stakes larger on higher-confidence signals; compounds on winning streaks'}
                  </p>
                  {compoundMode && (
                    <div className="pt-1">
                      <label className="text-xs text-emerald-300 flex items-center justify-between">
                        <span>🎲 Enable Martingale</span>
                        <button
                          onClick={() => setCompoundMartingale(!compoundMartingale)}
                          className={`relative w-8 h-4 rounded-full transition-colors ${compoundMartingale ? 'bg-emerald-400' : 'bg-gray-700'}`}
                          data-testid="compound-martingale-toggle"
                        >
                          <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${compoundMartingale ? 'translate-x-4' : ''}`}></div>
                        </button>
                      </label>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {compoundMartingale
                          ? 'After losses, stake grows by martingale × confidence × win-streak bonus. Cap: 5× base.'
                          : 'Off: only confidence + win-streak scale the stake. On: also recover losses.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Digit Mode Toggle - EVEN/ODD Contracts */}
                <div className="space-y-1 p-2 border border-cyan-600 rounded bg-cyan-900/20">
                  <label className="text-xs text-cyan-400 flex items-center justify-between">
                    <span>🎯 Digit Mode (Even/Odd)</span>
                    <button
                      onClick={() => { setDigitMatchMode(!digitMatchMode); if (!digitMatchMode) { setScalperMode(false); setTrendRiderMode(false); setAccumulatorMode(false); setCompoundMode(false); } }}
                      className={`relative w-8 h-4 rounded-full transition-colors ${digitMatchMode ? 'bg-cyan-500' : 'bg-gray-600'}`}
                      data-testid="digit-match-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${digitMatchMode ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                  <p className="text-xs text-gray-500">
                    {digitMatchMode
                      ? (aiDigitMode
                          ? 'Claude studies the digit stream and devises its own digit trades'
                          : 'XX double-digit: 2 instant trades on the next 2 ticks after any doubled digit')
                      : 'Standard analysis mode'}
                  </p>
                  {digitMatchMode && (
                    <div className="pt-1 space-y-1">
                      <label className="text-xs text-cyan-300 flex items-center justify-between">
                        <span>🤖 AI Digit Strategy</span>
                        <button
                          onClick={() => setAiDigitMode(!aiDigitMode)}
                          className={`relative w-8 h-4 rounded-full transition-colors ${aiDigitMode ? 'bg-cyan-400' : 'bg-gray-600'}`}
                          data-testid="ai-digit-mode-toggle"
                        >
                          <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${aiDigitMode ? 'translate-x-4' : ''}`}></div>
                        </button>
                      </label>
                      <p className="text-[10px] text-cyan-300/60">
                        {aiDigitMode
                          ? 'Claude analyses digit frequency, streaks & even/odd bias every 60s → picks Over/Under, Even/Odd or Differs trades (≥70% confidence only)'
                          : 'OFF — uses the fixed XX double-digit pattern below'}
                      </p>
                      {!aiDigitMode && (
                        <>
                          <label className="text-[10px] uppercase text-cyan-300 tracking-wider">XX Pattern Filter</label>
                          <select
                            value={digitXxFilter}
                            onChange={(e) => setDigitXxFilter(e.target.value)}
                            className="w-full bg-gray-900 border border-cyan-700 text-cyan-200 text-xs rounded px-2 py-1"
                            data-testid="digit-xx-filter-select"
                          >
                            <option value="any">Any XX pair (all doubles)</option>
                            <option value="high">HIGH pairs only (7,7 / 8,8 / 9,9)</option>
                            <option value="low">LOW pairs only (0,0 / 1,1 / 2,2)</option>
                          </select>
                          <p className="text-[10px] text-cyan-300/60">
                            High pairs → OVER 7 · Low pairs → UNDER 2
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* SCALPER MODE - Fast micro-reversals */}
                <div className="space-y-1 p-2 border border-pink-600 rounded bg-pink-900/20">
                  <label className="text-xs text-pink-400 flex items-center justify-between">
                    <span>🎯 Scalper Mode (Fast)</span>
                    <button
                      onClick={() => { 
                        setScalperMode(!scalperMode); 
                        if (!scalperMode) { 
                          setDigitMatchMode(false); 
                          setTrendRiderMode(false); 
                          setAccumulatorMode(false);
                          setCompoundMode(false);
                        } 
                      }}
                      className={`relative w-8 h-4 rounded-full transition-colors ${scalperMode ? 'bg-pink-500' : 'bg-gray-600'}`}
                      data-testid="scalper-mode-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${scalperMode ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                  <p className="text-xs text-gray-500">
                    {scalperMode ? 'Quick reversals using RSI7 + Short BB' : 'Fast in, fast out micro-trades'}
                  </p>
                </div>

                {/* TREND RIDER MODE - Follow strong trends */}
                <div className="space-y-1 p-2 border border-blue-600 rounded bg-blue-900/20">
                  <label className="text-xs text-blue-400 flex items-center justify-between">
                    <span>📈 Trend Rider Mode</span>
                    <button
                      onClick={() => { 
                        setTrendRiderMode(!trendRiderMode); 
                        if (!trendRiderMode) { 
                          setDigitMatchMode(false); 
                          setScalperMode(false); 
                          setAccumulatorMode(false);
                          setCompoundMode(false);
                        } 
                      }}
                      className={`relative w-8 h-4 rounded-full transition-colors ${trendRiderMode ? 'bg-blue-500' : 'bg-gray-600'}`}
                      data-testid="trend-rider-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${trendRiderMode ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                  <p className="text-xs text-gray-500">
                    {trendRiderMode
                      ? (aiTrendMode
                          ? 'Claude finds clear trends with slight pullbacks and rides the continuation'
                          : 'EMA alignment + ADX trend strength')
                      : 'Follow strong trends, skip ranging markets'}
                  </p>
                  {trendRiderMode && (
                    <div className="pt-1 space-y-1">
                      <label className="text-xs text-blue-300 flex items-center justify-between">
                        <span>🤖 AI Trend Strategy</span>
                        <button
                          onClick={() => setAiTrendMode(!aiTrendMode)}
                          className={`relative w-8 h-4 rounded-full transition-colors ${aiTrendMode ? 'bg-blue-400' : 'bg-gray-600'}`}
                          data-testid="ai-trend-mode-toggle"
                        >
                          <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${aiTrendMode ? 'translate-x-4' : ''}`}></div>
                        </button>
                      </label>
                      <p className="text-[10px] text-blue-300/60">
                        {aiTrendMode
                          ? 'Claude checks EMAs, ADX & pullback position every 60s → enters CALL/PUT only when a clear trend pulls back slightly (≥70% confidence)'
                          : 'OFF — uses the fixed EMA + ADX scoring engine'}
                      </p>
                    </div>
                  )}
                </div>

                {/* ACCUMULATOR MODE - Deriv ACCU contracts on low-vol markets */}
                <div className="space-y-1 p-2 border border-teal-500 rounded bg-teal-900/25">
                  <label className="text-xs text-teal-300 flex items-center justify-between">
                    <span>📊 Accumulator Mode</span>
                    <button
                      onClick={() => {
                        setAccumulatorMode(!accumulatorMode);
                        if (!accumulatorMode) {
                          setDigitMatchMode(false);
                          setScalperMode(false);
                          setTrendRiderMode(false);
                          setCompoundMode(false);
                        }
                      }}
                      className={`relative w-8 h-4 rounded-full transition-colors ${accumulatorMode ? 'bg-teal-500' : 'bg-gray-600'}`}
                      data-testid="accumulator-mode-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${accumulatorMode ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                  <p className="text-xs text-gray-500">
                    {accumulatorMode
                      ? `ACCU ${Math.round(accumulatorGrowthRate * 100)}% growth on low-vol markets · TP at ${Math.round(accumulatorTpPercent * 100)}% of stake`
                      : 'Compounding gains on consolidating markets — best win-rate strategy'}
                  </p>
                  {accumulatorMode && (
                    <div className="pt-1 grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] uppercase text-teal-300 tracking-wider">Growth Rate</label>
                        <select
                          value={accumulatorGrowthRate}
                          onChange={(e) => setAccumulatorGrowthRate(parseFloat(e.target.value))}
                          className="w-full bg-gray-900 border border-teal-700 text-teal-100 text-xs h-7 rounded px-1"
                          data-testid="accumulator-growth-rate"
                        >
                          <option value={0.01}>1% (safest — widest barrier)</option>
                          <option value={0.02}>2%</option>
                          <option value={0.03}>3%</option>
                          <option value={0.04}>4%</option>
                          <option value={0.05}>5% (aggressive)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase text-teal-300 tracking-wider">TP (% of stake)</label>
                        <Input
                          type="number"
                          min="1"
                          max="200"
                          step="1"
                          value={Math.round(accumulatorTpPercent * 100)}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value || '10');
                            setAccumulatorTpPercent(Math.max(0.01, Math.min(2.0, v / 100)));
                          }}
                          className="bg-gray-900 border-teal-700 text-teal-100 text-xs h-7"
                          data-testid="accumulator-tp-percent"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Stake (min $0.35)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.35"
                    value={stake}
                    onChange={(e) => setStake(Math.max(parseFloat(e.target.value) || 0.35, 0.35))}
                    className="bg-gray-800 border-teal-700 text-white text-xs h-6"
                    data-testid="stake-input"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-gray-400 flex items-center justify-between">
                    <span>Martingale {martingaleEnabled ? `: ${martingaleMultiplier.toFixed(2)}x` : 'OFF'}</span>
                    <button
                      onClick={() => setMartingaleEnabled(!martingaleEnabled)}
                      className={`relative w-8 h-4 rounded-full transition-colors ${martingaleEnabled ? 'bg-teal-500' : 'bg-gray-600'}`}
                      data-testid="martingale-toggle"
                    >
                      <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${martingaleEnabled ? 'translate-x-4' : ''}`}></div>
                    </button>
                  </label>
                  {martingaleEnabled && (
                    <Slider
                      value={[martingaleMultiplier]}
                      onValueChange={(value) => setMartingaleMultiplier(value[0])}
                      min={1.1}
                      max={2.5}
                      step={0.05}
                      className="w-full h-4"
                      data-testid="martingale-slider"
                    />
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Max Stake (0=No Limit)</label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={martingaleMaxStake}
                    onChange={(e) => setMartingaleMaxStake(parseFloat(e.target.value) || 0)}
                    className="bg-gray-800 border-teal-700 text-white text-xs h-6"
                    data-testid="max-stake-input"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Min Momentum (0=Any)</label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    value={minMomentum}
                    onChange={(e) => setMinMomentum(parseFloat(e.target.value) || 0)}
                    className="bg-gray-800 border-teal-700 text-white text-xs h-6"
                    placeholder="e.g. 0.010"
                    data-testid="min-momentum-input"
                  />
                </div>

                {/* RSI Filter Dropdown */}
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">RSI Filter</label>
                  <select
                    value={rsiFilter}
                    onChange={(e) => setRsiFilter(e.target.value)}
                    className="w-full bg-gray-800 border border-teal-700 text-white text-xs h-6 rounded px-1"
                    data-testid="rsi-filter-select"
                  >
                    <option value="off">Off (Any RSI)</option>
                    <option value="under_75">Under 75 Only</option>
                    <option value="over_75">Over 75 Only</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-1">
                  <div className="space-y-1">
                    <label className="text-xs text-gray-400">Take Profit</label>
                    <Input
                      type="number"
                      step="10"
                      min="0"
                      value={takeProfit}
                      onChange={(e) => setTakeProfit(parseFloat(e.target.value))}
                      className="bg-gray-800 border-teal-700 text-white text-xs h-6"
                      data-testid="take-profit-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-400">Stop Loss</label>
                    <Input
                      type="number"
                      step="10"
                      min="0"
                      value={stopLoss}
                      onChange={(e) => setStopLoss(parseFloat(e.target.value))}
                      className="bg-gray-800 border-teal-700 text-white text-xs h-6"
                      data-testid="stop-loss-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleUpdateConfig}
                  className="w-full bg-teal-600 hover:bg-teal-700 h-6 text-xs"
                >
                  Update
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Statistics */}
          {authenticated && (
            <Card className="bg-gray-900 border-teal-700 p-2">
              <CardHeader className="p-2">
                <CardTitle className="text-sm text-teal-400">Stats</CardTitle>
              </CardHeader>
              <CardContent className="p-2 pt-0">
                <div className="space-y-1">
                  <div className="flex justify-between items-center pb-1 border-b border-teal-700">
                    <span className="text-xs text-gray-400">Profit</span>
                    <span className={`text-sm font-bold ${(stats.profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="total-profit">
                      {(stats.profit || 0) >= 0 ? '+' : ''}{(stats.profit || 0).toFixed(2)} {currency}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-400">Trades</span>
                    <span className="text-xs font-bold text-white">{stats.total_trades || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-400">Win Rate</span>
                    <span className="text-xs font-bold text-green-400">{winRate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-400">W/L</span>
                    <span className="text-xs font-bold text-white">
                      <span className="text-green-400">{stats.wins || 0}</span>
                      {'/'}
                      <span className="text-red-400">{stats.losses || 0}</span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-400">Streak</span>
                    <div className="flex items-center gap-1">
                      {(stats.consecutive_losses || 0) >= 3 && (
                        <span className="text-xs bg-orange-600 text-white px-1 py-0.5 rounded animate-pulse">
                          RECOVERY
                        </span>
                      )}
                      <span className={`text-xs font-bold ${(stats.consecutive_losses || 0) >= 3 ? 'text-orange-400' : (stats.consecutive_losses || 0) >= 2 ? 'text-red-400' : 'text-yellow-400'}`}>
                        {stats.consecutive_losses || 0}L
                      </span>
                    </div>
                  </div>
                  {stats.session_count > 0 && (
                    <div className="flex justify-between pt-1 border-t border-teal-700">
                      <span className="text-xs text-gray-400">Sessions</span>
                      <span className="text-xs font-bold text-teal-400">#{stats.session_count || 0}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Columns - Expanded */}
        <div className="lg:col-span-3 space-y-2 overflow-y-auto">
          {/* REAL-TIME MARKET ANALYSIS - Show after API connects */}
          {authenticated && (
            <Card className="bg-gray-900 border-teal-700 p-2">
              <CardHeader className="p-2">
                <CardTitle className="text-sm text-teal-400 flex items-center gap-2">
                  <Activity className="w-3 h-3" />
                  Live Markets
                  {!botRunning && Object.keys(analyzedMarkets).length > 0 && (
                    <span className="text-xs bg-teal-900 text-teal-300 px-2 py-0.5 rounded-full">
                      Scanning
                    </span>
                  )}
                  {botRunning && (
                    <span className="text-xs bg-green-900 text-green-300 px-2 py-0.5 rounded-full">
                      Trading
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 pt-0">
                {Object.keys(analyzedMarkets).length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <Activity className="w-6 h-6 mx-auto mb-2 animate-pulse" />
                    Loading market data...
                  </div>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {Object.entries(analyzedMarkets)
                      .sort(([,a], [,b]) => (b.score || b.momentum || 0) - (a.score || a.momentum || 0))
                      .map(([symbol, analysis]) => (
                        <div
                          key={symbol}
                          className={`p-2 rounded border ${
                            analysis.ready ? 'bg-teal-900/30 border-teal-600' : 'bg-gray-800 border-gray-700'
                          }`}
                      data-testid={`market-${symbol}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-white text-xs">{symbol}</span>
                          {analysis.ready && <span className="text-xs bg-green-600 px-1 py-0.5 rounded">READY</span>}
                          {analysis.confidence && analysis.confidence !== 'LOW' && (
                            <span className={`text-xs px-1 py-0.5 rounded ${
                              analysis.confidence === 'HIGH' ? 'bg-green-700 text-green-200' : 'bg-yellow-700 text-yellow-200'
                            }`}>
                              {analysis.confidence}
                            </span>
                          )}
                          {analysis.status && <span className="text-xs text-gray-500">{analysis.status}</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          {analysis.score !== undefined && (
                            <span className={`text-xs px-1 py-0.5 rounded ${
                              analysis.score >= 60 ? 'bg-green-900 text-green-400' :
                              analysis.score >= 40 ? 'bg-yellow-900 text-yellow-400' :
                              'bg-gray-700 text-gray-400'
                            }`}>
                              {analysis.score}pts
                            </span>
                          )}
                          {analysis.direction && (
                            <span className={`text-xs font-bold ${analysis.direction === 'CALL' ? 'text-green-400' : 'text-red-400'}`}>
                              {analysis.direction}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-1 text-xs">
                        <div>
                          <div className="text-gray-500 text-xs">Mom</div>
                          <div className={`font-mono text-xs ${analysis.momentum > 0 ? 'text-teal-400' : 'text-red-400'}`}>
                            {analysis.momentum > 0 ? '+' : ''}{analysis.momentum?.toFixed(4) || '0.0000'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">RSI</div>
                          <div className={`font-mono text-xs ${
                            analysis.rsi < 30 ? 'text-green-400' : 
                            analysis.rsi > 70 ? 'text-red-400' : 'text-gray-300'
                          }`}>
                            {analysis.rsi?.toFixed(0) || '50'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">Trend</div>
                          <div className={`font-mono text-xs ${
                            analysis.trend === 'bullish' ? 'text-green-400' : 
                            analysis.trend === 'bearish' ? 'text-red-400' : 'text-gray-400'
                          }`}>
                            {analysis.trend || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">Prob</div>
                          <div className={`font-mono text-xs ${
                            analysis.probability >= 65 ? 'text-green-400' : 
                            analysis.probability >= 55 ? 'text-yellow-400' : 'text-gray-400'
                          }`}>
                            {analysis.probability?.toFixed(0) || '50'}%
                          </div>
                        </div>
                      </div>
                      {analysis.signals && analysis.signals.length > 0 && (
                        <div className="mt-1 text-xs text-teal-500 truncate">
                          {analysis.signals.join(' • ')}
                        </div>
                      )}
                    </div>
                  ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Trade History */}
          {authenticated && (
            <Card className="bg-gray-900 border-teal-700 p-2">
              <CardHeader className="p-2">
                <CardTitle className="text-sm text-teal-400">Trades</CardTitle>
              </CardHeader>
              <CardContent className="p-2 pt-0">
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {trades.length === 0 ? (
                    <p className="text-gray-500 text-center py-2 text-xs">No trades yet</p>
                  ) : (
                    trades.map((trade, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-2 rounded border ${
                          trade.result === 'win' ? 'bg-green-900/20 border-green-700' : 
                          trade.result === 'loss' ? 'bg-red-900/20 border-red-700' : 
                          trade.status === 'pending' ? 'bg-blue-900/20 border-blue-700' :
                          'bg-gray-800 border-gray-700'
                        }`}
                        data-testid={`trade-row-${index}`}
                      >
                        <div className="flex items-center gap-2">
                          {trade.direction === 'CALL' ? (
                            <TrendingUp className="w-3 h-3 text-green-400" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-red-400" />
                          )}
                          <div>
                            <div className="text-white font-medium flex items-center gap-1 text-xs">
                              {trade.symbol}
                              {trade.mode && (
                                <span className="text-[9px] px-1 py-0.5 rounded bg-gray-700 text-gray-300">{trade.mode}</span>
                              )}
                              {trade.status === 'pending' && (
                                <span className="text-[9px] text-blue-300 animate-pulse">● LIVE</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400">
                              {new Date(trade.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <div>
                            <div className="text-white font-medium text-xs">
                              {trade.stake?.toFixed(1) || '0.0'} {currency}
                            </div>
                            {trade.status === 'pending' && trade.floating_profit !== undefined && trade.floating_profit !== null && (
                              <div className={`text-xs font-bold ${trade.floating_profit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid={`floating-profit-${index}`}>
                                Floating: {trade.floating_profit >= 0 ? '+' : ''}{Number(trade.floating_profit).toFixed(2)}
                              </div>
                            )}
                            {trade.pnl !== undefined && trade.status !== 'pending' && (
                              <div className={`text-xs font-bold ${trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {trade.pnl >= 0 ? '+' : ''}{trade.pnl.toFixed(1)}
                              </div>
                            )}
                            {trade.result && (
                              <div className={`text-xs font-bold uppercase ${
                                trade.result === 'win' ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {trade.result}
                              </div>
                            )}
                          </div>
                          {trade.status === 'pending' && trade.contract_id && (
                            <button
                              onClick={() => handleCloseTrade(trade.contract_id)}
                              disabled={closingContracts.has(trade.contract_id)}
                              className="text-[10px] px-2 py-1 rounded bg-red-600 hover:bg-red-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold"
                              data-testid={`close-trade-btn-${index}`}
                            >
                              {closingContracts.has(trade.contract_id) ? '…' : 'Close'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Live Digit Stream (only in Digit Match Mode) */}
          {digitMatchMode && Object.keys(recentDigits).length > 0 && (
            <Card className="bg-gray-900 border-cyan-700 p-2" data-testid="digit-stream-card">
              <CardHeader className="p-2">
                <CardTitle className="text-sm text-cyan-400 flex items-center justify-between">
                  <span>Live Digits</span>
                  <span className="text-[10px] text-gray-500 font-normal">
                    <span className="text-red-400">●</span> low (0-2) &nbsp;
                    <span className="text-gray-300">●</span> mid (3-6) &nbsp;
                    <span className="text-green-400">●</span> high (7-9)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 pt-0 space-y-1">
                {Object.entries(recentDigits).map(([sym, digits]) => (
                  <div key={sym} className="flex items-center gap-2" data-testid={`digit-stream-${sym}`}>
                    <div className="text-[10px] text-gray-400 font-mono w-14 shrink-0">{sym}</div>
                    <div className="flex flex-wrap gap-0.5">
                      {digits.map((d, i) => {
                        const isLow = d <= 2;
                        const isHigh = d >= 7;
                        const isLast = i === digits.length - 1;
                        const color = isLow ? 'bg-red-500 text-white'
                                    : isHigh ? 'bg-green-500 text-white'
                                    : 'bg-gray-700 text-gray-200';
                        return (
                          <span
                            key={i}
                            className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-mono ${color} ${isLast ? 'ring-2 ring-cyan-300' : ''}`}
                            title={isLast ? 'Latest tick' : ''}
                          >
                            {d}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Activity Log */}
          <Card className="bg-gray-900 border-teal-700 p-2">
            <CardHeader className="p-2">
              <CardTitle className="text-sm text-teal-400">Activity</CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0">
              <div className="space-y-1 max-h-48 overflow-y-auto font-mono text-xs">
                {logs.length === 0 ? (
                  <p className="text-gray-500 text-center py-2 text-xs">No activity yet</p>
                ) : (
                  logs.map((log, index) => (
                    <div
                      key={index}
                      className={`p-1 rounded text-xs ${
                        log.type === 'error' ? 'bg-red-900/20 text-red-400' :
                        log.type === 'success' ? 'bg-green-900/20 text-green-400' :
                        log.type === 'warning' ? 'bg-yellow-900/20 text-yellow-400' :
                        'bg-gray-800 text-gray-300'
                      }`}
                    >
                      <span className="text-gray-500">[{new Date(log.timestamp).toLocaleTimeString()}]</span> {log.message}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===== Settings Modal (Mirror Accounts) ===== */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="settings-modal"
        >
          <div className="bg-gray-900 border border-teal-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-teal-700 px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-bold text-teal-400 flex items-center gap-2">
                <Settings className="w-4 h-4" /> Settings
              </h2>
              <button
                onClick={() => { setShowSettings(false); setMirrorError(''); }}
                className="text-gray-400 hover:text-white text-2xl leading-none"
                data-testid="close-settings-button"
              >
                ×
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Mirror Accounts Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-orange-400">🪞 Mirror Accounts</h3>
                  <span className="text-xs text-gray-400">{mirrors.length} / {maxMirrors}</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Every trade placed on your primary account will be mirrored on each connected mirror (same stake).
                  If any mirror trade fails, the bot stops automatically.
                </p>

                {/* Current mirrors */}
                {mirrors.length === 0 && (
                  <p className="text-xs text-gray-600 italic mb-3">No mirror accounts connected yet.</p>
                )}
                {mirrors.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between p-2 mb-2 border border-orange-900/50 rounded bg-orange-900/10"
                    data-testid={`mirror-row-${m.id}`}
                  >
                    <div className="text-xs">
                      <div className="text-orange-300 font-semibold">{m.label} <span className="text-gray-500">({m.auth_method})</span></div>
                      <div className="text-gray-400">{m.loginid} • {m.currency} {Number(m.balance).toFixed(2)}</div>
                      {m.status !== 'connected' && (
                        <div className="text-red-400 text-[10px] mt-0.5">{m.status}: {m.last_error || 'unknown error'}</div>
                      )}
                    </div>
                    <Button
                      onClick={() => removeMirror(m.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-700 text-red-400 hover:bg-red-900 h-7 px-2 text-xs"
                      data-testid={`remove-mirror-${m.id}-button`}
                    >
                      Remove
                    </Button>
                  </div>
                ))}

                {/* Add mirror form */}
                {mirrors.length < maxMirrors && (
                  <div className="border border-orange-700 rounded p-3 bg-orange-900/10 space-y-2">
                    <div className="text-xs font-semibold text-orange-300">Add New Mirror</div>
                    <Input
                      type="password"
                      placeholder="Deriv API Token (legacy or pat_…)"
                      value={newMirrorToken}
                      onChange={(e) => setNewMirrorToken(e.target.value)}
                      className="bg-gray-800 border-orange-700 text-white text-xs h-8"
                      disabled={mirrorAdding}
                      data-testid="mirror-token-input"
                    />
                    <Input
                      type="text"
                      placeholder="Account ID (optional, e.g. CR1234567 or DOT…)"
                      value={newMirrorAccountId}
                      onChange={(e) => setNewMirrorAccountId(e.target.value)}
                      className="bg-gray-800 border-orange-700 text-white text-xs h-8"
                      disabled={mirrorAdding}
                      data-testid="mirror-account-id-input"
                    />
                    <Input
                      type="text"
                      placeholder="Label (optional, e.g. 'Brother account')"
                      value={newMirrorLabel}
                      onChange={(e) => setNewMirrorLabel(e.target.value)}
                      className="bg-gray-800 border-orange-700 text-white text-xs h-8"
                      disabled={mirrorAdding}
                      data-testid="mirror-label-input"
                    />
                    {mirrorError && (
                      <div className="text-xs text-red-400 bg-red-900/30 border border-red-700 rounded px-2 py-1" data-testid="mirror-error">
                        {mirrorError}
                      </div>
                    )}
                    <Button
                      onClick={addMirror}
                      className="w-full bg-orange-600 hover:bg-orange-700 h-8 text-xs"
                      disabled={!newMirrorToken || mirrorAdding}
                      data-testid="add-mirror-button"
                    >
                      {mirrorAdding ? 'Connecting…' : 'Add Mirror'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
